/**
 * LaneHeader — Aggregat-Ring + Leitkennzahl, beide aus `PipelineLane`.
 *
 * Das „Update" in UpdateLanes: der Bahnkopf trägt keine Konstante, sondern
 * rechnet je Stand aus `pipeline.json`. Kennzahl UND Aggregat stehen im
 * barrierefreien Namen der Bahn — die Aussage darf nicht nur im Ring stecken.
 *
 * Warum eigenes SVG statt `DonutChart` (prince-ui charts/charts.tsx:312) —
 * geprüft, vier harte Gründe:
 *   1. `total <= 0` rendert `ChartEmpty` (Icon + Text) statt eines leeren
 *      Rings. Genau der leere Ring ist hier aber ein Zustand („nie gelaufen").
 *   2. `.prn-chart-donut > .prn-chart` ist auf `width: 160px` festgenagelt und
 *      liegt in einem Flex-Container mit Legende — kein 30-px-Chip.
 *   3. Das Zentrum läuft durch ein `foreignObject` mit
 *      `font: var(--prn-text-title-3)`; bei 30 px ist das unlesbar.
 *   4. Das SVG ist hart `aria-hidden` ohne Weg, einen Namen zu setzen.
 * Segmentfarben ließen sich zwar als `color: "var(--prn-green)"` übergeben —
 * das ist aber der einzige Punkt, der passt. Deshalb 12 Zeilen eigenes SVG mit
 * `stroke-dasharray`, exakt wie in preview-hig.html.
 */
import { cx } from "@conuti-das/prince-ui";
import type { PipelineLane, ReleaseKey } from "@/types/pipeline";
import {
  FLOW_STATES,
  formatCount,
  tallyTotal,
  tallyWords,
  type FlowTally,
  type FlowVocabulary,
} from "./flowState";
import "./flow.css";

/** Tokenname je Zustand — keine Hex-Literale, hell wie dunkel tragfähig. */
const TONE_VAR: Record<string, string> = {
  ok: "--prn-green",
  running: "--prn-orange",
  failed: "--prn-red",
  sealed: "--prn-blue",
  open: "--prn-label-3",
  skipped: "--prn-separator",
  ghost: "--prn-separator",
};

export interface AggregateRingProps {
  tally: FlowTally;
  /** Kantenlänge in px. Default 30. */
  size?: number;
  /** Barrierefreier Name; ohne ihn ist der Ring dekorativ. */
  label?: string;
  className?: string;
}

/** Ring über die Zustände einer Bahn; in der Mitte die Zahl der erledigten. */
export function AggregateRing({ tally, size = 30, label, className }: AggregateRingProps) {
  const total = tallyTotal(tally) || 1;
  const r = 11;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  const segments = FLOW_STATES.filter((s) => (tally[s] ?? 0) > 0).map((s) => {
    const len = ((tally[s] ?? 0) / total) * circumference;
    const seg = {
      state: s,
      // 1,2 px Fuge zwischen den Segmenten, damit sie auch einfarbig zählbar sind.
      dash: `${(len - 1.2).toFixed(2)} ${(circumference - len + 1.2).toFixed(2)}`,
      offset: -offset,
    };
    offset += len;
    return seg;
  });
  const done = tally.ok ?? 0;

  return (
    <span className={cx("flow-ring", className)} style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 30 30"
        width={size}
        height={size}
        role={label ? "img" : undefined}
        aria-hidden={label ? undefined : true}
        aria-label={label}
        focusable="false"
      >
        {label && <title>{label}</title>}
        <circle className="flow-ring-track" cx="15" cy="15" r={r} />
        {segments.map((s) => (
          <circle
            key={s.state}
            className="flow-ring-seg"
            cx="15"
            cy="15"
            r={r}
            stroke={`var(${TONE_VAR[s.state]})`}
            strokeDasharray={s.dash}
            strokeDashoffset={s.offset.toFixed(2)}
            transform="rotate(-90 15 15)"
          />
        ))}
      </svg>
      <span className="flow-ring-center">{done}</span>
    </span>
  );
}

/**
 * Der barrierefreie Name einer Bahn — Kennzahl UND Aggregat, z. B.
 * „Generatoren — 3 Generatoren; 3 erledigt, 6 noch nicht erreicht".
 * Die Oberfläche setzt ihn auf ihr `role="group"`-Element.
 */
export function laneAriaLabel(
  lane: PipelineLane,
  release: ReleaseKey,
  vocabulary: FlowVocabulary = "run",
): string {
  const metric = lane.metric?.[release];
  const words = tallyWords(lane.aggregate?.[release] ?? {}, vocabulary);
  const head = `${lane.label} — ${formatCount(metric)} ${lane.metricLabel}`;
  return words ? `${head}; ${words}` : head;
}

export interface LaneHeaderProps {
  lane: PipelineLane;
  release: ReleaseKey;
  /** `run` = Laufzustand (Startseite), `build` = Bauzustand (Architektur). */
  vocabulary?: FlowVocabulary;
  /** Zoom 1: nur Ring, Titel und Kennzahl — ohne Untertitel. */
  compact?: boolean;
  ringSize?: number;
  className?: string;
}

export function LaneHeader({
  lane,
  release,
  vocabulary = "run",
  compact,
  ringSize = 30,
  className,
}: LaneHeaderProps) {
  const tally = lane.aggregate?.[release] ?? {};
  const metric = lane.metric?.[release];
  const words = tallyWords(tally, vocabulary);

  return (
    <div className={cx("flow-lane-head", className)} data-compact={compact ? "" : undefined}>
      <AggregateRing tally={tally} size={ringSize} label={words || undefined} />
      <span className="flow-lane-text">
        <span className="flow-line flow-lane-title">{lane.label}</span>
        {!compact && <span className="flow-line flow-lane-sub">{lane.sub}</span>}
        <span className="flow-lane-metric">
          {formatCount(metric)}
          <span className="flow-lane-unit">{lane.metricLabel}</span>
        </span>
      </span>
    </div>
  );
}
